/**
 * CheckEmailPage Component
 * Page displayed after registration to guide users through email verification
 */

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useEmailVerification } from '../../../hooks/email-verification';
import { useEmailConfirmationStatus } from '../../../hooks/email-verification/useEmailConfirmationStatus';
import { EmailSentIllustration } from './EmailSentIllustration';
import { EmailInstructions } from './EmailInstructions';
import { ResendEmailButton } from './ResendEmailButton';

export const CheckEmailPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const email = location.state?.email;
  // const fromRegistration = location.state?.fromRegistration || false; // Future use
  const [pendingAuth, setPendingAuth] = useState(location.state?.pendingAuth || false);
  
  // Check email confirmation status
  const { status, isConfirmed } = useEmailConfirmationStatus();
  
  // Initialize email verification hook (must be called before any returns)
  const {
    resendEmail,
    isResending,
    canResend,
    cooldownSeconds,
    attempts,
    maxAttempts
  } = useEmailVerification(email || '');

  // FIXED: Move all hooks to top before any conditional returns
  const [pendingTimeout, setPendingTimeout] = useState(false);

  // All useEffect hooks must be called before any conditional returns
  // REMOVED: Auto-redirect logic to allow EmailConfirmedPage to show
  // Email confirmation flow now goes: CheckEmailPage → EmailConfirmedPage → Manual click → Dashboard
  
  // Redirect if no email provided (safety check)
  useEffect(() => {
    if (!email && !isLoading) {
      navigate('/register', { replace: true });
    }
  }, [email, navigate, isLoading]);

  // Clear pendingAuth state after registration completes (3 seconds max)
  useEffect(() => {
    if (pendingAuth) {
      const timer = setTimeout(() => {
        setPendingAuth(false);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [pendingAuth]);

  // FIXED: Move pending timeout effect to top with other hooks
  useEffect(() => {
    if (status === 'pending') {
      // Set timeout to prevent infinite pending state
      const timer = setTimeout(() => {
        console.warn('⚠️ Pending status timeout - forcing normal view');
        setPendingTimeout(true);
      }, 5000);
      
      return () => clearTimeout(timer);
    } else {
      setPendingTimeout(false);
    }
  }, [status]);

  // Navigate to email confirmed page when status changes to confirmed
  useEffect(() => {
    if (isConfirmed) {
      // Check if this is a fresh email confirmation or just a page refresh
      const alreadyProcessed = sessionStorage.getItem('email_confirmation_processed');
      const isFromEmailLink = window.location.search.includes('type=signup') || 
                             window.location.hash.includes('type=signup');
      
      // Only navigate if this is a fresh confirmation from email link
      if (!alreadyProcessed || isFromEmailLink) {
        console.log('📧 Fresh email confirmation detected, navigating to email-confirmed');
        navigate('/email-confirmed', { replace: true });
      } else {
        console.log('📧 Email already processed, redirecting to dashboard');
        navigate('/dashboard', { replace: true });
      }
    }
  }, [isConfirmed, navigate]);

  // Conditional rendering after all hooks are called
  // If email is confirmed, show loading while navigating
  if (isConfirmed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Redirecting...</p>
        </div>
      </div>
    );
  }

  // Show pending state during registration process (with timeout)
  if (status === 'pending' && !pendingTimeout) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-3xl shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Menyelesaikan Pendaftaran...
            </h2>
            <p className="text-sm text-gray-600">
              Mohon tunggu sebentar, kami sedang memproses akun Anda dan mengirim email konfirmasi.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Don't render if no email
  if (!email) {
    return null;
  }

  // Show loading state if registration is still pending
  if (pendingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-3xl shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Menyelesaikan Pendaftaran...
            </h2>
            <p className="text-sm text-gray-600">
              Mohon tunggu sebentar, kami sedang memproses akun Anda.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Column - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600 p-12 flex-col justify-center items-center relative overflow-hidden">
        {/* Decorative Blur Circles */}
        <div className="absolute -top-20 -left-20 w-[500px] h-[500px] bg-blue-400/30 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 -right-20 w-[400px] h-[400px] bg-indigo-400/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/3 right-1/4 w-[300px] h-[300px] bg-purple-400/20 rounded-full blur-3xl"></div>
        
        <EmailSentIllustration />
      </div>

      {/* Right Column - Check Email Content */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-3xl shadow-lg p-6 md:p-8">
            {/* Email Instructions */}
            <EmailInstructions email={email} />
            
            {/* Bonus Member Baru Info */}
            <div className="mt-5 p-3 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0">
                  <img 
                    src="https://res.cloudinary.com/dubmxw6kl/image/upload/v1765420357/Desain_tanpa_judul_lx8vrv.jpg" 
                    alt="Bonus Icon"
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-xs font-semibold text-yellow-800">
                  Bonus Member Baru Menanti!
                </p>
              </div>
              <p className="text-xs text-yellow-700">
                Setelah konfirmasi email, jangan lupa klaim bonus spesial untuk member baru.
              </p>
            </div>

            {/* Resend Email Button */}
            <div className="mt-5">
              <ResendEmailButton 
                onResend={resendEmail}
                canResend={canResend}
                cooldownSeconds={cooldownSeconds}
                isLoading={isResending}
                attempts={attempts}
                maxAttempts={maxAttempts}
              />
            </div>

            {/* Compact Additional Actions */}
            <div className="mt-5 pt-4 border-t border-gray-200 space-y-2">
              {/* Consolidated Links */}
              <div className="text-center space-y-1">
                <div>
                  <span className="text-xs text-gray-600">Email salah? </span>
                  <Link to="/register" className="text-xs text-blue-600 hover:text-blue-700 font-medium">
                    Daftar ulang
                  </Link>
                </div>
                <div>
                  <span className="text-xs text-gray-600">Sudah punya akun? </span>
                  <Link to="/login" className="text-xs text-blue-600 hover:text-blue-700 font-medium">
                    Masuk
                  </Link>
                </div>
                <div>
                  <span className="text-xs text-gray-500">Butuh bantuan? </span>
                  <a href="mailto:support@canvango.com" className="text-xs text-blue-600 hover:text-blue-700 font-medium">
                    Hubungi Support
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Add default export for lazy loading compatibility
export default CheckEmailPage;